import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom-v6'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@clouway/ui'
import { Download, Pencil, Plus, Trash2 } from 'lucide-react'

import { serviceAccountKeyUrl } from '../api/api'
import { useDeleteServiceAccount, useServiceAccounts } from '../hooks'

export function ServiceAccountsList() {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { data: accounts, isLoading } = useServiceAccounts()
  const { mutate: remove } = useDeleteServiceAccount()

  if (isLoading) {
    return <Skeleton className="mx-auto h-64 w-full max-w-3xl" />
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
      <div className="flex justify-end">
        <Button asChild>
          <Link to="/service-accounts/new">
            <Plus className="size-4" />
            {t('serviceAccount.list.add')}
          </Link>
        </Button>
      </div>

      {accounts && accounts.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('serviceAccount.list.empty')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('serviceAccount.list.name')}</TableHead>
              <TableHead>{t('serviceAccount.list.email')}</TableHead>
              <TableHead>{t('serviceAccount.list.issuedOn')}</TableHead>
              <TableHead className="w-28" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts?.map((account) => (
              <TableRow key={account.id}>
                <TableCell className="font-medium">{account.name}</TableCell>
                <TableCell className="font-mono text-xs">{account.clientEmail}</TableCell>
                <TableCell>{account.issuedOn}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button asChild size="icon" variant="ghost" aria-label={t('serviceAccount.list.downloadKey')}>
                      <a href={serviceAccountKeyUrl(account.id)} download>
                        <Download className="size-4" />
                      </a>
                    </Button>
                    <Button asChild size="icon" variant="ghost" aria-label="edit">
                      <Link to={`/service-accounts/${account.id}/edit`}>
                        <Pencil className="size-4" />
                      </Link>
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" aria-label="delete">
                          <Trash2 className="size-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('serviceAccount.list.delete')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('serviceAccount.list.deleteDescription', { account: account.name })}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(account.id)}>
                            {tCommon('ui.button.confirm')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
